// Procedural ambient background music. Three "tracks" — really three
// continuous synth voicings — one per difficulty phase. Each phase change
// cross-fades smoothly. Pure Web Audio: no asset files.
//
// Voicing recipe per track:
//   - sustained chord on triangle oscillators (root + 3rd + 5th)
//   - slow LFO on each oscillator's frequency for a gentle wobble
//   - lowpassed sawtooth pad an octave below for body
//
// Three keys / moods:
//   early : A minor open chord     calm, sparse
//   mid   : D minor with 7th       slightly anxious
//   late  : F# minor with #5       dissonant, urgent

import type { Phase } from '../sim/systems/wave-director';

export interface MusicVoicing {
  /** Bass note (Hz). */
  root: number;
  /** Stacked notes built on top of root (Hz). */
  upper: number[];
  /** Master gain for this voicing. */
  gain: number;
  /** LFO depth in Hz on each oscillator. */
  lfoDepth: number;
  /** LFO rate in Hz. */
  lfoRate: number;
  /** Lowpass cutoff for the bass pad. */
  cutoff: number;
}

export const VOICINGS: Record<Phase, MusicVoicing> = {
  early: {
    root: 110,
    upper: [220, 261.63, 329.63], // A3 + A4 + C5 + E5  (A minor)
    gain: 0.18,
    lfoDepth: 0.6,
    lfoRate: 0.18,
    cutoff: 600,
  },
  mid: {
    root: 146.83,
    upper: [293.66, 349.23, 440], // D3 + D4 + F4 + A4  (D minor)
    gain: 0.22,
    lfoDepth: 1.0,
    lfoRate: 0.26,
    cutoff: 800,
  },
  late: {
    root: 184.99,
    upper: [369.99, 440, 554.37], // F#3 + F#4 + A4 + C#5  (tense)
    gain: 0.26,
    lfoDepth: 1.8,
    lfoRate: 0.35,
    cutoff: 1100,
  },
};

interface Track {
  master: GainNode;
  stop: () => void;
}

/** MusicEngine owns one continuously-playing voice per phase. Each voice
 *  is on a master gain node that we cross-fade. */
export class MusicEngine {
  private ctx: AudioContext | null = null;
  private tracks: Partial<Record<Phase, Track>> = {};
  private currentPhase: Phase | null = null;
  private masterGain = 1;

  start(ctx: AudioContext, out: AudioNode, initialPhase: Phase): void {
    if (this.ctx) return;
    this.ctx = ctx;
    // Build all three voices but mute everything; we'll fade in the active one.
    for (const phase of ['early', 'mid', 'late'] as const) {
      this.tracks[phase] = buildVoice(ctx, out, VOICINGS[phase]);
      // Start silent.
      this.tracks[phase]!.master.gain.value = 0;
    }
    this.currentPhase = initialPhase;
    this.fade(initialPhase, 1.0, 0.3);
  }

  /** Switch active phase with a cross-fade. Idempotent for the same phase. */
  setPhase(phase: Phase, fadeSec: number = 2): void {
    if (!this.ctx) return;
    if (phase === this.currentPhase) return;
    if (this.currentPhase) this.fade(this.currentPhase, 0, fadeSec);
    this.fade(phase, 1, fadeSec);
    this.currentPhase = phase;
  }

  /** Master attenuation applied on top of per-phase volume. 0..1. */
  setMasterGain(g: number): void {
    this.masterGain = clamp01(g);
    if (this.currentPhase) {
      const t = this.tracks[this.currentPhase];
      if (t) t.master.gain.value = this.masterGain;
    }
  }

  stop(fadeSec: number = 1): void {
    if (!this.ctx) return;
    for (const t of Object.values(this.tracks)) {
      if (!t) continue;
      const now = this.ctx.currentTime;
      t.master.gain.cancelScheduledValues(now);
      t.master.gain.setValueAtTime(t.master.gain.value, now);
      t.master.gain.linearRampToValueAtTime(0, now + fadeSec);
      // Stop oscillators after the fade — schedule via stop() handle.
      setTimeout(() => t.stop(), Math.max(0, fadeSec * 1000 + 50));
    }
    this.tracks = {};
    this.currentPhase = null;
    this.ctx = null;
  }

  private fade(phase: Phase, target: number, durSec: number): void {
    if (!this.ctx) return;
    const t = this.tracks[phase];
    if (!t) return;
    const now = this.ctx.currentTime;
    t.master.gain.cancelScheduledValues(now);
    t.master.gain.setValueAtTime(t.master.gain.value, now);
    t.master.gain.linearRampToValueAtTime(target * this.masterGain, now + durSec);
  }
}

function buildVoice(ctx: AudioContext, out: AudioNode, v: MusicVoicing): Track {
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(out);

  const voiceGain = ctx.createGain();
  voiceGain.gain.value = v.gain;
  voiceGain.connect(master);

  const stops: Array<() => void> = [];

  // Stacked triangle oscillators for the chord notes.
  for (const f of v.upper) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = f;

    // Per-osc LFO on frequency.
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = v.lfoRate * (0.7 + Math.random() * 0.6);
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = v.lfoDepth;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);

    const g = ctx.createGain();
    g.gain.value = 0.18;
    osc.connect(g).connect(voiceGain);

    osc.start();
    lfo.start();
    stops.push(() => {
      try {
        osc.stop();
        lfo.stop();
      } catch {
        // Already stopped; ignore.
      }
    });
  }

  // Lowpassed sawtooth bass pad.
  const bass = ctx.createOscillator();
  bass.type = 'sawtooth';
  bass.frequency.value = v.root;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = v.cutoff;
  const bassGain = ctx.createGain();
  bassGain.gain.value = 0.2;
  bass.connect(lp).connect(bassGain).connect(voiceGain);
  bass.start();
  stops.push(() => {
    try {
      bass.stop();
    } catch {
      // Already stopped.
    }
  });

  return {
    master,
    stop: () => {
      for (const s of stops) s();
    },
  };
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
